import type { RumContext, RumTransitionOwner, RumTransitionPayload, RumTransitionSentReason } from './types';
import type { RumRouter, RumRouterNavigationEvent } from './session';

export type RumTransitionTrackerOptions = {
  send: (payload: RumTransitionPayload) => void;
  getContext: () => RumContext;
  /** The page load's session id — shared with the initial_load beacon so transitions join to it. */
  getSessionId: () => string;
  /** Classify who renders a destination pathname: 'hydra' or the embedded bundle. */
  getOwner: (pathname: string) => RumTransitionOwner;
  /**
   * Drawn once per navigation on an already-sampled page load: transitions
   * are far more frequent than page loads and each one is its own POST, so
   * they sample again on top of the session decision (see sampling.ts).
   */
  shouldSample: () => boolean;
  /**
   * Whether the initial_load beacon has flushed. Gates the embedded-sync path
   * only — see `recordEmbeddedSync` for why the router path's own marker
   * can't serve there.
   */
  isInitialLoadComplete: () => boolean;
  /** Flush a transition that never resolves after this long. */
  timeoutMs?: number;
};

const DEFAULT_TIMEOUT_MS = 30_000;

export type RumTransitionTracker = {
  /** Wire router subscriptions and tab-hide flushing. Call once, before first render. */
  install: (router: RumRouter) => void;
  /**
   * Report an intra-embedded-app navigation the shell only observes as a URL
   * sync (the embedded-history-sync bypass, which never runs the router's
   * navigation lifecycle). Emits a milestone-less beacon: the shell cannot see
   * the embedded app's render timing, so these are counted, not timed.
   * No-op until installed (unsampled loads never install).
   */
  recordEmbeddedSync: (toPathname: string) => void;
};

type PendingTransition = {
  navStartMs: number;
  fromRoute: string;
  toPathname: string;
  timer: ReturnType<typeof setTimeout>;
};

/**
 * One beacon per SPA navigation (`kind: "transition"`), unlike the one-shot
 * initial_load session. A transition is bracketed by the router's
 * `onBeforeNavigate` → `onResolved` events — `onResolved` fires when React
 * finished rendering the destination, so `settled` covers route loading and
 * render, not post-render data fetches (a claim milestone is the planned next
 * step). Search-param and hash navigations are not transitions and are
 * filtered out via `pathChanged`; so is the initial load (its timings belong
 * to the initial_load beacon). Interrupted transitions still report — as
 * `superseded`, `hidden`, or `timeout` — so slow navigations users abandon
 * don't vanish from the data.
 *
 * Navigations sample again on top of the page load's decision (`shouldSample`):
 * one POST per navigation, with no batching, would otherwise make a heavy
 * navigator's sampled session far noisier than its page load.
 */
export function createRumTransitionTracker(options: RumTransitionTrackerOptions): RumTransitionTracker {
  const {
    send,
    getContext,
    getSessionId,
    getOwner,
    shouldSample,
    isInitialLoadComplete,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = options;

  let installed = false;
  let router: RumRouter | null = null;
  let pending: PendingTransition | null = null;
  // The first onResolved is the initial load settling, not a transition.
  let initialLoadResolved = false;

  function routePattern(): string {
    const matches = router?.state.matches;
    // No pathname fallback: raw paths carry ids and become metric labels.
    return matches?.[matches.length - 1]?.routeId ?? 'unknown';
  }

  function flush(transition: PendingTransition, reason: RumTransitionSentReason, settledMs?: number) {
    clearTimeout(transition.timer);
    if (pending === transition) {
      pending = null;
    }

    // Telemetry must never break the app.
    try {
      send({
        v: 1,
        kind: 'transition',
        session_id: getSessionId(),
        ...getContext(),
        owner: getOwner(transition.toPathname),
        // Until the destination settles, the router's matches still hold the
        // origin route — there is no committed destination pattern to report.
        to_route: settledMs == null ? 'unknown' : routePattern(),
        from_route: transition.fromRoute,
        nav_start_ms: transition.navStartMs,
        milestones: settledMs == null ? {} : { settled: settledMs },
        sent_reason: reason,
      });
    } catch {
      // swallow
    }
  }

  function onNavigationStart(event: RumRouterNavigationEvent) {
    // The initial load's navigation is not a transition; search-param and
    // hash-only navigations (e.g. nuqs query-state updates) aren't either.
    if (!initialLoadResolved || !event.pathChanged) {
      return;
    }
    // Supersede before the sampling draw, never after: leaving a sampled
    // pending transition in place because *this* navigation went unsampled
    // would settle it against this navigation's resolve — the wrong
    // destination and a duration spanning both.
    if (pending != null) {
      flush(pending, 'superseded');
    }
    if (!shouldSample()) {
      return;
    }
    const transition: PendingTransition = {
      navStartMs: performance.now(),
      fromRoute: routePattern(),
      toPathname: event.toLocation.pathname,
      timer: setTimeout(() => {
        if (pending === transition) {
          flush(transition, 'timeout');
        }
      }, timeoutMs),
    };
    pending = transition;
  }

  function onNavigationResolved(event: RumRouterNavigationEvent) {
    if (!initialLoadResolved) {
      initialLoadResolved = true;
      return;
    }
    if (pending == null) {
      return;
    }
    // A search/hash-only navigation can resolve while the path transition is
    // still loading (e.g. a nuqs update on the page being left) — settling on
    // it would truncate the duration and label the origin as the destination.
    // The pending transition settles only on a path resolve. Deliberately
    // `pathChanged` and not a pathname comparison: a redirect mid-transition
    // (`/workspaces/last/…`) settles on a pathname the start never saw.
    if (!event.pathChanged) {
      return;
    }
    flush(pending, 'settled', performance.now() - pending.navStartMs);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden' && pending != null) {
      flush(pending, 'hidden');
    }
  }

  return {
    install(appRouter) {
      if (installed) {
        return;
      }
      installed = true;
      router = appRouter;

      appRouter.subscribe('onBeforeNavigate', onNavigationStart);
      appRouter.subscribe('onResolved', onNavigationResolved);
      document.addEventListener('visibilitychange', onVisibilityChange);
    },
    recordEmbeddedSync(toPathname) {
      if (!installed) {
        return;
      }
      // Boot-time URL normalization is not a transition. An embedded app
      // rewrites the URL as it boots (`/workspaces/last/journeys` →
      // `/workspaces/123/journeys/dashboard`), which is part of the initial
      // load the initial_load beacon already measures — reporting it here
      // would double-count it as a navigation the user never made.
      //
      // The router path's `initialLoadResolved` can't serve as the marker:
      // it flips when the *shell* renders the embedded host, seconds before
      // the embedded app boots and does its rewriting. The initial_load
      // beacon's flush is the marker that outlasts the child app's boot, and
      // it's the same boundary the requests beacon uses to separate
      // `initial_load` from `transition`.
      //
      // Cost, accepted: a genuine intra-app navigation made before that flush
      // is dropped rather than reported. Those are navigations away from a
      // page that was still settling, so they'd be poor measurements anyway —
      // and dropping is safer than minting phantom transitions on every load.
      if (!isInitialLoadComplete()) {
        return;
      }
      // A sync can only land while a hydra transition is pending in odd
      // interleavings, but a stale pending must not later "settle" against
      // the wrong destination — close it out first, before the draw below
      // can return early.
      if (pending != null) {
        flush(pending, 'superseded');
      }
      // Intra-embedded navigations are the highest-volume kind, so they take
      // the same per-transition draw.
      if (!shouldSample()) {
        return;
      }

      // Telemetry must never break the app.
      try {
        send({
          v: 1,
          kind: 'transition',
          session_id: getSessionId(),
          ...getContext(),
          owner: getOwner(toPathname),
          // The sync path never recomputes matches, so the pattern is the
          // embedded splat route both ways — which is the honest label: the
          // shell only knows "somewhere within this app".
          to_route: routePattern(),
          from_route: routePattern(),
          nav_start_ms: performance.now(),
          milestones: {},
          sent_reason: 'embedded_sync',
        });
      } catch {
        // swallow
      }
    },
  };
}
